/* eslint-disable typescript-enum/no-enum */

import { Maybe, Optional } from '@vimeo/core/types/generic';
import { ContentRating as ContentRatingType } from '@vimeo/gctl-api/models/components/schemas/content-rating/ContentRating';
import { EventSchedule as LiveEventSchedule } from '@vimeo/gctl-api/models/components/schemas/event-schedule/EventSchedule';
import { Event as LiveEvent } from '@vimeo/gctl-api/models/components/schemas/event/Event';
import { AccountType, User } from '@vimeo/viewer';

import { EComposerStreamModeType } from './composer_session';

export type ViewPrivacy = NonNullable<LiveEvent['streamPrivacy']>['view'];

export type StreamingMethod = LiveEvent['preferredStreamMethod'];

export type Latency = LiveEvent['latency'];

export type EmbedPrivacy = NonNullable<LiveEvent['streamPrivacy']>['embed'];

export type ContentRating = ContentRatingType['code'];

export type AutoCcLanguage = LiveEvent['autoCcLanguage'];

/**
 * Type of widget to use when rendering event settings component.
 */
export type ViewType = 'dropdown' | 'radio' | 'list';

// todo: Enum object?
export const EEventStreamingMethodVariant: Record<Capitalize<StreamingMethod>, StreamingMethod> = {
  Encoder: 'encoder',
  Browser: 'browser',
} as const;

// todo: Enum object?
export const EEventLatency: Record<string, Latency> = {
  Standard: 'standard',
  Low: 'low',
  FailSafe: 'fail-safe',
} as const;

export enum ESettingsLocation {
  Wizard,
  Settings,
}

/**
 * Cloud studio ingest status from live API.
 */
export enum EIngestStatus {
  UNKNOWN = -1,
  UNAVAILABLE = 0,
  PROVISION_REQUESTED = 1,
  PROVISION_READY = 2,
  PROVISION_EXPIRED = 3,
  STREAMING = 4,
  ENDED = 5,
}

/**
 * Cloud studio active encoder from live API.
 */
export enum EEncoderType {
  UNKNOWN = 'unknown',
  WEBRTC = 'webrtc',
  RTMP = 'rtmp',
  DASH = 'dash',
  SRT = 'srt',
  SIMPLE_LIVE = 'simple_live',
}

export enum EArchiveStatus {
  NOT_STARTED = 0,
  STARTED = 1,
  DONE = 2,
  ERROR = 3,
}

export enum EStreamPrivacy {
  ANYBODY = 'anybody',
  EMBED_ONLY = 'embed_only',
  NOBODY = 'nobody',
  PASSWORD = 'password',
  UNLISTED = 'unlisted',
}

export enum EStreamEmbedPrivacy {
  PRIVATE = 'private',
  PUBLIC = 'public',
  WHITELIST = 'whitelist',
}

export interface IPicture {
  width: number;
  height: number;
  link: string;
}

export interface ILiveClip {
  id: number;
}

export interface IClip {
  uri: string;
  live: ILiveClip;
}

export interface IStreamedClip {
  clipId: number;
  streamingOn: number;
}

export enum EScheduleType {
  SINGLE = 'single',
  WEEKLY = 'weekly',
}

export interface IRecurringLiveEventSchedule {
  type: EScheduleType;
  weekdays: Array<number>;
  /**
   * The ISO 8601 time on which the event is expected to be live when **schedule.type** is `weekly`.
   */
  dailyTime: string;
  /**
   * The ISO 8601 date on which the first occurrence of the event is expected to be live when **schedule.type** is `weekly`.
   */
  scheduledTime: string;
  /**
   * The ISO 8601 date on which the event is expected to be live when **schedule.type** is `single`.
   */
  startTime?: string;
}

export interface IThumbnailPicture {
  width: number;
  height: number;
  link: string;
  linkWithPlayButton: string;
}

export interface IRecurringLiveEventMetadata {
  interactions: {
    delete: Optional<{
      uri: string;
    }>;
    edit: Optional<{
      uri: string;
    }>;
    activate: Optional<{
      uri: string;
    }>;
  };
}

export interface ILiveQuota {
  status: 'available' | 'private_mode' | 'stream_limit' | 'time_limit' | 'provision_limit';
  streams: {
    maximum: number;
    remaining: number;
  };
  hiResStreaming: {
    remaining: number;
    maximum: number;
  };
  hiResViewing: {
    remaining: number;
    maximum: number;
  };
}

export interface IRecurringLiveEventOwner extends User {
  account: AccountType;
  liveQuota?: ILiveQuota;
}

export interface IRecurringLiveEventAlbum {
  id: number;
  title: string;
  privacy: string;
  embedCode: string;
}

export interface IRecurringLiveEvent {
  album: Optional<IRecurringLiveEventAlbum>;
  allowedPrivacies: Array<EStreamPrivacy>;
  autoCcEnabled: boolean;
  autoCcLimit: number;
  autoCcKeywords: string;
  autoCcLanguage: AutoCcLanguage;
  autoCcRemaining: number;
  contentRating: Array<ContentRating>;
  dashLink: string;
  disableAutoArchiving?: boolean;
  dvr?: boolean;
  embed: {
    embedProperties: {
      sourceUrl: string;
    };
    chatEmbedSource: string;
  };
  fromPrivateToMeFolder: boolean;
  fromShowcase: boolean;
  hasRegistration: boolean;
  latency: Latency;
  link: string;
  liveClips: Array<IStreamedClip>;
  lowLatency: boolean;
  metadata: IRecurringLiveEventMetadata;
  parentFolder?: {
    name: string;
    uri: string;
    isPrivateToUser?: boolean;
  };
  pictures: {
    active: boolean;
    defaultPicture: boolean;
    sizes: Array<IThumbnailPicture>;
    type: string;
  };
  preferredStreamMethod: StreamingMethod;
  rtmpLink: string;
  rtmpPreview: boolean;
  rtmpsLink: string;
  schedule: Optional<LiveEventSchedule>;
  scheduledPlayback: boolean;
  srtEncryptionEnabled: boolean;
  srtLink: string;
  srtPassphrase: Optional<string>;
  status?: typeof ENTITY_STATUS[keyof typeof ENTITY_STATUS];
  streamDescription: string;
  streamKey: string;
  streamMode: EComposerStreamModeType;
  streamPassword: string;
  streamPrivacy: {
    view: EStreamPrivacy;
    embed: EStreamEmbedPrivacy;
    unlistedHash: Optional<string>;
  };
  streamTitle: string;
  streamableClip: IClip;
  streamingAutoStop?: boolean;
  timeZone: string;
  title: string;
  unlimitedAutoCc: boolean;
  unlimitedDuration?: boolean;
  user: IRecurringLiveEventOwner;
}

export const ENTITY_STATUS = {
  STARTED: 'started',
  ENDED: 'ended',
  EMPTY: '',
} as const;

export interface ILiveEventAutoCc {
  autoCcEnabled: boolean;
  autoCcKeywords: string;
  autoCcLanguage: string;
  autoCcRemaining: number;
  autoCcIsUnlimited: boolean;
  autoCcCanBeEnabled: boolean;
}

export interface ISessionMetering {
  secondsMax: number;
  secondsRemaining: number;
}

export interface ISessionInfo {
  id: number;
  canManage: boolean;
  streamMode: EComposerStreamModeType;
  ingest: {
    isRtmpSession: boolean;
    encoderType: EEncoderType;
    status: EIngestStatus;
    endTime: Optional<number>;
    width: Optional<number>;
    height: Optional<number>;
    startTime: Optional<number>;
    sessionId: Optional<string>;
    isScheduledPlayback: boolean;
    scheduledStartTime: Optional<string>;
    streamEndedReason: Optional<EStreamEndedReason>;
  };
  archive: {
    clipId: number;
    status: EArchiveStatus;
  };
  metering: ISessionMetering;
  status: Maybe<EEventCompletionStatus>;
}

export interface ILiveEventStats {
  plays: Maybe<number>;
  totalViewTime: Maybe<number>;
  viewers: {
    current: Maybe<number>;
    peak: Maybe<number>;
  };
}

export interface IEmbedWhitelistResponse {
  items: Array<{
    domain: string;
  }>;
}

export enum EEventCompletionStatus {
  STARTED = 'started',
  ENDED = 'ended',
}

export enum EStreamEndedReason {
  NORMAL = 6,
  RECORDING_WAS_DELETED = 7,
}
